import { Text, Checkbox, Flex, Input } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormField } from '../../../components/FormField'
import { FormModal } from '../../../components/FormModal'
import ImageCropInput from '../../../components/ImageCropInput/ImageCropInput'
import { FormSelect, SearchableSelect } from '../../../components/Select'
import { useAppToast } from '../../../helpers'
import { ISector, SectorService } from '../../../services/Sector/SectorService'
import { UserService } from '../../../services/User/UserService'
import { IUserGroup, UserGroupService } from '../../../services/UserGroup/UserGroupService'

interface IProps {
  isOpen: boolean
  onClose: () => void
}

interface IForm {
  name: string
  email: string
  password: string
  password_confirmation: string
  user_group_id: number
  sector_ids: number[]
  is_senior: boolean
}

const defaultForm: IForm = {
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  user_group_id: 0,
  sector_ids: [],
  is_senior: false
}

export function UserCreateModal({ isOpen, onClose }: IProps) {
  const userService = new UserService()
  const userGroupService = new UserGroupService()
  const sectorService = new SectorService()
  const [form, setForm] = useState<IForm>(defaultForm)
  const [image, setImage] = useState<File>()
  const [errors, setErrors] = useState<{ [key: string]: string[] }>({})
  const [userGroups, setUserGroups] = useState<IUserGroup[]>()
  const [sectors, setSectors] = useState<ISector[]>([])
  const [loadingSectors, setLoadingSectors] = useState(true)
  const toast = useAppToast()

  useEffect(() => {
    let isMounted = true
    const fetchUserGroups = async () => {
      try {
        await userGroupService.listForSelect().then((data) => {
          if (isMounted) {
            setUserGroups(data)
          }
        })
      } catch {
        toast.error(
          'Falha ao carregar grupos de usuários',
          'Ocorreu um erro ao buscar os grupos de usuários. Tente novamente mais tarde.'
        )
        setUserGroups([])
      }
    }

    const fetchSectors = async () => {
      setLoadingSectors(true)
      try {
        await sectorService.listForSelect().then((data) => {
          if (isMounted) {
            setSectors(data)
          }
        })
      } catch {
        toast.error('Falha ao carregar setores', 'Ocorreu um erro ao buscar os setores. Tente novamente mais tarde.')
        setSectors([])
      } finally {
        setLoadingSectors(false)
      }
    }

    const loadData = async () => {
      await fetchUserGroups()
      await fetchSectors()
      if (isMounted) {
        setForm(defaultForm)
      }
    }

    loadData()

    return () => {
      isMounted = false
    }
  }, [isOpen])

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!formValid() || image === undefined) {
      return
    }

    userService
      .create({ ...form, picture: image })
      .then(() => {
        toast.success('Usuário criado com sucesso')

        onClose()
      })
      .catch((error) => {
        setErrors(error.response.data.errors)
      })
  }

  const formValid = () => {
    return (
      form.name &&
      form.email &&
      form.password &&
      form.password_confirmation &&
      form.password === form.password_confirmation &&
      image !== undefined &&
      form.user_group_id > 0 &&
      form.sector_ids.length > 0
    )
  }

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Criar usuário"
      onSubmit={handleSave}
      isConfirmDisabled={!formValid()}
    >
      <Flex direction="column">
        <FormField label="Nome" error={errors.name} isRequired>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </FormField>

        <FormField label="Email" error={errors.email} isRequired mt={4}>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        </FormField>

        <FormField label="Senha" error={errors.password} isRequired mt={4}>
          <Input
            type="password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            autoComplete="new-password"
          />
        </FormField>

        <FormField
          label="Confirmação de senha"
          isRequired
          mt={4}
          mb={4}
          error={form.password !== form.password_confirmation ? 'As senhas não coincidem' : null}
        >
          <Input
            type="password"
            value={form.password_confirmation}
            onChange={(e) => setForm({ ...form, password_confirmation: e.target.value })}
          />
        </FormField>

        <ImageCropInput
          label="Foto de perfil"
          value={image ?? null}
          onChange={(file) => setImage(file)}
          previewHeight="100px"
          accept="photo"
          error={errors.picture}
        />

        <FormField label="Selecione qual o grupo do usuário" error={errors.user_group_id} mt={4}>
          <FormSelect
            value={form.user_group_id}
            onChange={(value) => setForm({ ...form, user_group_id: Number(value) })}
            options={userGroups?.map((userGroup) => ({ value: userGroup.id, label: userGroup.name })) ?? []}
            placeholder="Selecione uma opção"
            emptyValue={'0'}
          />
        </FormField>

        <FormField label="Selecione quais são os setores do usuário" error={errors.sector_ids} mt={4}>
          <SearchableSelect<number>
            isMulti
            options={sectors.map((sector) => ({ value: sector.id, label: sector.name }))}
            value={form.sector_ids ?? []}
            onChange={(sectorIds) => setForm({ ...form, sector_ids: sectorIds })}
            placeholder="Selecione um ou mais setores"
            entity="setor"
            isLoading={loadingSectors}
          />
        </FormField>
        <Checkbox mt={4} isChecked={form.is_senior} onChange={(e) => setForm({ ...form, is_senior: e.target.checked })}>
          Gestor/sênior
        </Checkbox>
      </Flex>
      {!formValid() && (
        <Text variant="caption" mt={4}>
          Preencha todos os campos acima para prosseguir
        </Text>
      )}
    </FormModal>
  )
}
